import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import cloudApi from '../../../services/cloudApi';
import DatabaseRow from './DatabaseRow';
import { transformGroupedTablesToSources, dateRangeToISO } from './utils';
import EmptyState from './EmptyState';

const PAGE_SIZE = 20;

const SORT_OPTIONS = [
  { value: 'latest_table_at-desc', label: 'Latest First' },
  { value: 'latest_table_at-asc', label: 'Oldest First' },
  { value: 'row_count-desc', label: 'Most Rows' },
  { value: 'row_count-asc', label: 'Least Rows' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
  { value: 'table_count-desc', label: 'Most Runs' },
  { value: 'table_count-asc', label: 'Least Runs' },
];

const ROW_COUNT_OPTIONS = [
  { value: '', label: 'Any size' },
  { value: '100', label: '100+ rows' },
  { value: '1000', label: '1K+ rows' },
  { value: '10000', label: '10K+ rows' },
];

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All time' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: '3months', label: 'Last 3 months' },
];

const TASK_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'extract_list', label: 'List Extraction' },
  { value: 'extract_page_details', label: 'Page Details' },
  { value: 'extract_emails', label: 'Email Extraction' },
  { value: 'extract_social_links', label: 'Social Links' },
  { value: 'extract_shopify', label: 'Shopify Extraction' },
  { value: 'extract_woocommerce', label: 'WooCommerce Extraction' },
  { value: 'extract_magento', label: 'Magento Extraction' },
  { value: 'extract_salesforce_commerce', label: 'Salesforce Commerce Extraction' },
];

const DATE_RANGE_LABELS = { week: 'Last 7 days', month: 'Last 30 days', '3months': 'Last 3 months' };
const TASK_TYPE_LABELS = { extract_list: 'List Extraction', extract_page_details: 'Page Details', extract_emails: 'Email Extraction', extract_social_links: 'Social Links', extract_shopify: 'Shopify Extraction', extract_woocommerce: 'WooCommerce Extraction', extract_magento: 'Magento Extraction', extract_salesforce_commerce: 'Salesforce Commerce Extraction' };
const ROW_COUNT_LABELS = { '100': '100+ rows', '1000': '1K+ rows', '10000': '10K+ rows' };

// ─── Domain Combobox ────────────────────────────────────────────────────────
const DomainCombobox = ({ selectedDomain, onSelect }) => {
  const [domainSearch, setDomainSearch] = useState('');
  const [domainResults, setDomainResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch domains on search change
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        setLoadingDomains(true);
        const response = await cloudApi.getGroupedDomains(domainSearch);
        setDomainResults(response.data || []);
      } catch {
        setDomainResults([]);
      } finally {
        setLoadingDomains(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [domainSearch, open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (domain) => {
    onSelect(domain);
    setDomainSearch('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex-1 sm:flex-none">
      <div className="relative">
        <svg className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3 h-3 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          placeholder={selectedDomain || "Filter by domain..."}
          value={domainSearch}
          onChange={(e) => setDomainSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
          }}
          className={`w-full sm:w-[160px] pl-7 pr-3 py-1.5 bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-[11px] text-gray-600 dark:text-gray-300
                     placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20
                     dark:focus:bg-white/[0.05] transition-all duration-200 hover:border-gray-300 dark:hover:border-white/[0.1] ${selectedDomain ? 'placeholder-gray-700 dark:placeholder-gray-200' : ''}`}
        />
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-full sm:w-[220px] bg-white dark:bg-gray-900/95 dark:backdrop-blur-xl border border-gray-200 dark:border-white/[0.06] rounded-xl shadow-lg dark:ring-1 dark:ring-inset dark:ring-white/[0.04] z-50 overflow-hidden">
          {loadingDomains ? (
            <div className="px-3 py-3 text-[11px] text-gray-400 text-center">Searching...</div>
          ) : domainResults.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-gray-400 text-center">
              {domainSearch ? 'No domains found' : 'Type to search domains'}
            </div>
          ) : (
            <div className="max-h-[200px] overflow-y-auto">
              {domainResults.map(({ domain, count }) => (
                <button
                  key={domain}
                  onClick={() => handleSelect(domain)}
                  className="w-full flex items-center justify-between px-3 py-2 text-[11px] text-gray-600 dark:text-gray-300
                             hover:bg-blue-50 dark:hover:bg-white/[0.06] transition-colors text-left"
                >
                  <span className="truncate">{domain}</span>
                  <span className="text-gray-400 dark:text-gray-500 ml-2 flex-shrink-0 tabular-nums">{count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Filter Select ──────────────────────────────────────────────────────────
const FilterSelect = ({ value, onChange, options, className = '' }) => (
  <div className={`relative ${className}`}>
    <select
      value={value}
      onChange={onChange}
      className="appearance-none w-full bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full pl-3 pr-7 py-1.5 text-[11px] text-gray-600 dark:text-gray-300
                 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20
                 transition-all duration-200 hover:border-gray-300 dark:hover:border-white/[0.1] cursor-pointer"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

// ─── Filter Chip ────────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10
                    text-[11px] text-blue-600 dark:text-blue-400 font-medium border border-blue-200 dark:border-blue-500/20
                    animate-in fade-in duration-150">
    {label}
    <button
      onClick={onRemove}
      className="hover:text-blue-800 dark:hover:text-blue-200 transition-colors ml-0.5"
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
);

// ─── Main Component ─────────────────────────────────────────────────────────
const DatabaseView = () => {
  const navigate = useNavigate();

  // Data state
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [historicalTablesCache, setHistoricalTablesCache] = useState({});

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [sortBy, setSortBy] = useState('latest_table_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [taskType, setTaskType] = useState('');
  const [minRows, setMinRows] = useState('');
  const [dateRange, setDateRange] = useState('');

  // Track whether initial load has completed (to distinguish empty DB from empty filters)
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [hasAnyData, setHasAnyData] = useState(false);

  // Refs
  const scrollContainerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const fetchGenRef = useRef(0); // generation counter for stale response detection

  // ─── Active filters (for chips) ─────────────────────────────────────────
  const activeFilters = useMemo(() => {
    const filters = [];
    if (selectedDomain) filters.push({ key: 'domain', label: selectedDomain, onRemove: () => setSelectedDomain('') });
    if (taskType) filters.push({ key: 'taskType', label: TASK_TYPE_LABELS[taskType], onRemove: () => setTaskType('') });
    if (minRows) filters.push({ key: 'minRows', label: ROW_COUNT_LABELS[minRows], onRemove: () => setMinRows('') });
    if (dateRange) filters.push({ key: 'dateRange', label: DATE_RANGE_LABELS[dateRange], onRemove: () => setDateRange('') });
    return filters;
  }, [selectedDomain, taskType, minRows, dateRange]);

  const hasActiveFilters = searchTerm || activeFilters.length > 0;

  // ─── Debounce search ────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ─── Build server filter params ─────────────────────────────────────────
  const buildFilterParams = useCallback(() => {
    const params = { limit: PAGE_SIZE, sortBy, sortOrder };
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    if (selectedDomain) params.domain = selectedDomain;
    if (taskType) params.taskType = taskType;
    if (minRows) params.minRows = parseInt(minRows, 10);
    if (dateRange) {
      const { from, to } = dateRangeToISO(dateRange);
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
    }
    return params;
  }, [debouncedSearchTerm, selectedDomain, sortBy, sortOrder, taskType, minRows, dateRange]);

  // ─── Load first page ───────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    try {
      setLoading(true);
      setError(null);

      const params = { ...buildFilterParams(), page: 1 };
      const response = await cloudApi.getTablesGrouped(params);

      // Discard if a newer request was started
      if (gen !== fetchGenRef.current) return;

      const transformed = transformGroupedTablesToSources(response.data);
      setTasks(transformed);
      setPage(1);
      setTotalCount(response.pagination.total);
      setHasMore(response.pagination.hasNext);

      if (!initialLoadDone) {
        setHasAnyData(response.pagination.total > 0);
        setInitialLoadDone(true);
      }
      // On refresh without filters, update hasAnyData
      if (!hasActiveFilters) {
        setHasAnyData(response.pagination.total > 0);
      }
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      console.error('Failed to load tasks:', err);
      setError(err.message || 'Failed to load tasks');
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
      }
    }
  }, [buildFilterParams, initialLoadDone, hasActiveFilters]);

  // ─── Load next page (infinite scroll) ──────────────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const gen = fetchGenRef.current; // don't increment — we're appending to the current generation
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const params = { ...buildFilterParams(), page: nextPage };
      const response = await cloudApi.getTablesGrouped(params);

      if (gen !== fetchGenRef.current) return;

      const transformed = transformGroupedTablesToSources(response.data);
      setTasks(prev => [...prev, ...transformed]);
      setPage(nextPage);
      setHasMore(response.pagination.hasNext);
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      console.error('Failed to load more:', err);
    } finally {
      if (gen === fetchGenRef.current) {
        setLoadingMore(false);
      }
    }
  }, [page, hasMore, loadingMore, buildFilterParams]);

  // ─── Reload on filter change ────────────────────────────────────────────
  useEffect(() => {
    loadTasks();
  }, [debouncedSearchTerm, selectedDomain, sortBy, sortOrder, taskType, minRows, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── IntersectionObserver for infinite scroll ───────────────────────────
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    const container = scrollContainerRef.current;
    if (!trigger || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  // ─── Handlers ───────────────────────────────────────────────────────────
  const handleToggleExpanded = useCallback((taskId) => {
    setExpandedTaskId(prev => prev === taskId ? null : taskId);
  }, []);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedDomain('');
    setSortBy('latest_table_at');
    setSortOrder('desc');
    setTaskType('');
    setMinRows('');
    setDateRange('');
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  // Show header whenever we know data exists (even if current filter returns 0 results)
  const showHeader = initialLoadDone && hasAnyData;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden gap-3">
      {/* Header */}
      {showHeader && (
        <div className="px-1 mb-3 lg:mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">

            {/* Left: Title + Count */}
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                Database
              </h1>
              <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/[0.06] text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                {totalCount}
              </span>
            </div>

            {/* Right: Search + Filters + Sort + Refresh */}
            <div className="flex items-center gap-2 md:ml-auto flex-wrap md:flex-nowrap">
              {/* Search Input */}
              <div className="w-full md:w-auto md:min-w-[180px] lg:min-w-[200px] relative">
                <svg className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-7 py-1.5 bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-xs text-gray-700 dark:text-gray-300
                             placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20
                             dark:focus:bg-white/[0.05] transition-all duration-200 hover:border-gray-300 dark:hover:border-white/[0.1]"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Domain Combobox */}
              <DomainCombobox selectedDomain={selectedDomain} onSelect={setSelectedDomain} />

              <div className="hidden lg:block h-4 w-px bg-gray-300 dark:bg-white/[0.06]" />

              {/* Sort */}
              <FilterSelect
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                options={SORT_OPTIONS}
                className="flex-1 sm:flex-none"
              />

              {/* More Filters (task type, row count, date range) */}
              <FilterSelect value={taskType} onChange={(e) => setTaskType(e.target.value)} options={TASK_TYPE_OPTIONS} className="hidden lg:block" />
              <FilterSelect value={minRows} onChange={(e) => setMinRows(e.target.value)} options={ROW_COUNT_OPTIONS} className="hidden lg:block" />
              <FilterSelect value={dateRange} onChange={(e) => setDateRange(e.target.value)} options={DATE_RANGE_OPTIONS} className="hidden lg:block" />

            </div>
          </div>

          {/* Filter Chips */}
          {activeFilters.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
              {activeFilters.map(filter => (
                <FilterChip key={filter.key} label={filter.label} onRemove={filter.onRemove} />
              ))}
              <button
                onClick={handleClearFilters}
                className="text-[11px] text-gray-400 hover:text-rose-400 transition-colors font-medium ml-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto custom-scrollbar">
        {loading && !initialLoadDone ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="relative">
              <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 dark:border-white/[0.06] border-t-cyan-500"></div>
            </div>
            <p className="mt-4 text-xs text-gray-600 dark:text-gray-500 font-medium tracking-wide">Loading tasks...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/20 mb-3">
              <svg className="w-5 h-5 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="text-rose-400 text-sm font-medium mb-1">Failed to load tasks</div>
            <div className="text-gray-600 dark:text-gray-500 text-xs mb-4 max-w-xs">{error}</div>
            <button
              onClick={() => loadTasks()}
              className="px-3 py-1.5 rounded-lg bg-gray-200/60 dark:bg-white/[0.04] border border-gray-300 dark:border-white/[0.06]
                         text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-400 dark:hover:border-white/[0.1]
                         transition-all duration-200 text-xs font-medium"
            >
              Try again
            </button>
          </div>
        ) : !hasAnyData && initialLoadDone ? (
          <EmptyState onNavigate={() => navigate('/dashboard/automations')} />
        ) : tasks.length === 0 && hasActiveFilters ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="p-2.5 rounded-lg bg-gray-100 dark:bg-white/[0.04] border border-gray-300 dark:border-white/[0.06] mb-3">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">No tasks match your filters</div>
            <button
              onClick={handleClearFilters}
              className="mt-2 px-2 py-1 text-[11px] text-blue-400 hover:text-blue-300 transition-all duration-200
                         flex items-center gap-1 rounded-md hover:bg-blue-500/10"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className={`space-y-4 transition-opacity duration-150 ${loading && initialLoadDone ? 'opacity-60' : 'opacity-100'}`}>
            {tasks.map((task) => (
              <DatabaseRow
                key={task.id}
                task={task}
                isExpanded={expandedTaskId === task.id}
                onToggleExpanded={handleToggleExpanded}
                historicalTablesCache={historicalTablesCache}
                setHistoricalTablesCache={setHistoricalTablesCache}
              />
            ))}

            {/* Infinite scroll sentinel */}
            <div ref={loadMoreTriggerRef} className="h-10 flex items-center justify-center">
              {loadingMore && (
                <div className="animate-spin rounded-full h-5 w-5 border border-gray-300 dark:border-white/[0.06] border-t-cyan-500" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results Count Footer */}
      {!loading && !error && tasks.length > 0 && (
        <div className="mt-3 px-1 py-2">
          <div className="text-[11px] text-gray-600 dark:text-gray-500 font-medium">
            Showing <span className="text-gray-500 dark:text-gray-400">{tasks.length}</span>
            {totalCount > 0 && (
              <> of <span className="text-gray-600 dark:text-gray-300">{totalCount}</span></>
            )} sources
            {hasMore && <span className="text-gray-400"> (scroll for more)</span>}
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(75, 85, 99, 0.4);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(75, 85, 99, 0.6);
          }
          `,
        }}
      />
    </div>
  );
};

export default DatabaseView;
